import { useState } from 'react'
import { Link, useMatchRoute } from '@tanstack/react-router'
import {
  Home,
  Users,
  BarChart3,
  Clock,
  Layers,
  DollarSign,
  LogOut,
  User,
  Target,
  Settings,
  Wallet,
  Receipt,
  Package,
  Image as ImageIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSidebarStore } from '@/stores/sidebar-store'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { LogoutDialog } from '@/features/auth/components/logout-dialog'

interface NavItem {
  label: string
  href: string
  icon: typeof Home
  menuKey: string
}

const navItems: NavItem[] = [
  { label: 'Home', href: '/home', icon: Home, menuKey: 'home' },
  { label: 'Financeiro', href: '/financeiro', icon: DollarSign, menuKey: 'financeiro' },
  { label: 'Clientes', href: '/clientes', icon: Users, menuKey: 'clientes' },
  { label: 'Relatório Vendas', href: '/relatorio-vendas', icon: BarChart3, menuKey: 'relatorio-vendas' },
  { label: 'Dados Históricos', href: '/dados-historicos', icon: Clock, menuKey: 'dados-historicos' },
  { label: 'Grupos', href: '/grupos', icon: Layers, menuKey: 'grupos' },
  { label: 'Metas Loja', href: '/metas-loja', icon: Target, menuKey: 'metas-loja' },
  { label: 'Metas Individuais', href: '/metas-individuais', icon: Users, menuKey: 'metas-individuais' },
  { label: 'Fluxo de Caixa', href: '/fluxo-caixa', icon: Wallet, menuKey: 'fluxo-caixa' },
  { label: 'DRE', href: '/dre', icon: Receipt, menuKey: 'dre' },
  { label: 'Estoque', href: '/estoque', icon: Package, menuKey: 'estoque' },
  { label: 'Fotos', href: '/fotos', icon: ImageIcon, menuKey: 'fotos' },
  { label: 'Configurações', href: '/configuracoes', icon: Settings, menuKey: 'configuracoes' },
]

const roleLabels: Record<string, string> = {
  admin: 'Administrador',
  vendedor: 'Vendedor',
  gerencia: 'Gerência',
  contabilidade: 'Contabilidade',
}

/** Retorna o nome da permissão do usuário (prioriza dados do join com tabela permissao) */
function getPermissaoLabel(profile: { permissao?: { permissao: string | null } | null; role?: string } | null): string {
  if (profile?.permissao?.permissao) {
    const perm = profile.permissao.permissao
    return perm.charAt(0).toUpperCase() + perm.slice(1)
  }
  if (profile?.role) {
    return roleLabels[profile.role] || profile.role
  }
  return 'Carregando...'
}

const LOGO_URL = 'https://srwzlazgkldflwbcpgwm.supabase.co/storage/v1/object/public/geral/logo1_marrom_sem_fundo.png'

export function MobileSidebar() {
  const { closeMobile } = useSidebarStore()
  const { profile, hasMenuAccess } = useAuth()
  const matchRoute = useMatchRoute()
  const [showLogoutDialog, setShowLogoutDialog] = useState(false)

  // Filtra itens de navegação baseado nos acessos do usuário
  const filteredNavItems = navItems.filter((item) => hasMenuAccess(item.menuKey))

  const getInitials = (name: string | null | undefined): string => {
    if (!name) return 'U'
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div className="flex h-full flex-col bg-card">
      {/* Logo */}
      <div className="flex h-16 items-center border-b px-4">
        <Link to="/home" className="flex items-center gap-2" onClick={closeMobile}>
          <img src={LOGO_URL} alt="Bárbara Monteiro" className="h-10 object-contain" />
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-4">
        <ul className="space-y-1">
          {filteredNavItems.map((item) => {
            const isActive = matchRoute({ to: item.href, fuzzy: true })
            return (
              <li key={item.href}>
                <Link
                  to={item.href}
                  onClick={closeMobile}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  <span>{item.label}</span>
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>

      {/* User Profile Footer */}
      <div className="border-t p-3">
        <div className="flex items-center gap-3 rounded-lg px-3 py-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={undefined} alt={profile?.nome ?? ''} />
            <AvatarFallback>{getInitials(profile?.nome)}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col overflow-hidden">
            <span className="text-sm font-medium truncate">{profile?.nome ?? 'Usuário'}</span>
            <Badge variant="secondary" className="text-xs w-fit mt-0.5">
              {getPermissaoLabel(profile)}
            </Badge>
          </div>
        </div>

        <Separator className="my-3" />

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            asChild
          >
            <Link to="/perfil" onClick={closeMobile}>
              <User className="h-4 w-4 mr-2" />
              Perfil
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-destructive hover:text-destructive"
            onClick={() => setShowLogoutDialog(true)}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sair
          </Button>
        </div>
      </div>

      <LogoutDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog} />
    </div>
  )
}
